import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import SwitchField from '@/common/components/forms/switch-field';
import ToggleType from '../../toggle-type';
import CategoriesField from '@/common/components/forms/categories-field';
import WeekdayPicker from '@/common/components/forms/weekday-picker';
import formsStyle from '@/common/components/forms/forms.module.scss';
import InputField from '@/common/components/forms/input-field';
import { evaluateCalc } from '@/utils/calc';
import ToggleField from '@/common/components/forms/toggle-field';
import { defaultRecurrence, expandWithRecurrence } from './recurrence';
function useDebouncedChanged(value, onChanged, delayMs) {
    const onChangedRef = useRef(onChanged);
    const isFirstRenderRef = useRef(true);
    useEffect(() => {
        onChangedRef.current = onChanged;
    }, [onChanged]);
    useEffect(() => {
        if (!onChangedRef.current) {
            return;
        }
        if (isFirstRenderRef.current) {
            isFirstRenderRef.current = false;
            return;
        }
        const timerId = window.setTimeout(() => {
            onChangedRef.current?.(value);
        }, delayMs);
        return () => {
            window.clearTimeout(timerId);
        };
    }, [value, delayMs]);
}
function buildTransactions(params) {
    const { desc, amount, type, isPlanned, txDate, category, fromSavings } = params;
    const num = evaluateCalc(amount) ?? parseFloat(amount);
    if (Number.isNaN(num) || num <= 0) {
        return [];
    }
    const defaultDesc = type === 'income' ? 'Пополнение' : type === 'savings' ? 'Накопление' : 'Расход';
    const txs = [];
    if (fromSavings && type === 'income') {
        txs.push({
            date: txDate,
            description: desc.trim() || 'Из накоплений',
            planned: isPlanned ? num : null,
            actual: isPlanned ? null : num,
            type: 'savings',
            category: category || null,
            fromSavings: true,
        });
        return txs;
    }
    txs.push({
        date: txDate,
        description: desc.trim() || defaultDesc,
        planned: isPlanned ? num : null,
        actual: isPlanned ? null : num,
        type,
        category: category || null,
    });
    return txs;
}
export default function TransactionForm({ date, 
//   budget,
onChanged, changeDebounceMs = 250, preType, categories, savingsEnabled, weekStart, }) {
    const [desc, setDesc] = useState('');
    const [amount, setAmount] = useState('');
    const [type, setType] = useState(preType || 'expense');
    const [isPlanned, setIsPlanned] = useState(true);
    const [txDate, setTxDate] = useState(date);
    const [category, setCategory] = useState('');
    const [fromSavings, setFromSavings] = useState(false);
    const [recurrence, setRecurrence] = useState(defaultRecurrence);
    useEffect(() => {
        setTxDate(date);
    }, [date]);
    useEffect(() => {
        if (preType) {
            setType(preType);
        }
    }, [preType]);
    const transactions = useMemo(() => expandWithRecurrence(buildTransactions({
        desc,
        amount,
        type,
        isPlanned,
        txDate,
        category,
        fromSavings,
    }), recurrence), [desc, amount, type, isPlanned, txDate, category, fromSavings, recurrence]);
    useDebouncedChanged(transactions, onChanged, changeDebounceMs);
    const availableCategories = categories.filter((c) => c.type === (fromSavings && type === 'income' ? 'savings' : type));
    const descPlaceholder = type === 'income'
        ? 'Зарплата, бонус…'
        : type === 'savings'
            ? 'Цель, вклад…'
            : 'Продукты, кафе…';
    return (_jsxs(_Fragment, { children: [_jsx("div", { "data-tut": "tx-type", children: _jsx(ToggleType, { selectedType: type, onSelectedType: (selectedType) => {
                        setType(selectedType);
                        setCategory('');
                        if (selectedType !== 'income') {
                            setFromSavings(false);
                        }
                    }, hasSavings: savingsEnabled || false }) }), type === 'income' && savingsEnabled && (_jsx(SwitchField, { isOn: fromSavings, onToggle: (val) => { setFromSavings(val); setCategory(''); }, label: "\u0418\u0437 \u043D\u0430\u043A\u043E\u043F\u043B\u0435\u043D\u0438\u0439", description: "\u0412\u043E\u0437\u0432\u0440\u0430\u0442 \u0441\u0440\u0435\u0434\u0441\u0442\u0432 \u0438\u0437 \u0441\u0431\u0435\u0440\u0435\u0436\u0435\u043D\u0438\u0439 \u0432 \u0431\u044E\u0434\u0436\u0435\u0442", style: {
                    padding: '8px 0 10px',
                    borderBottom: '1px solid var(--border-light)',
                    marginBottom: 10,
                } })), _jsx("div", { "data-tut": "tx-category", children: _jsx(CategoriesField, { label: "\u041A\u0430\u0442\u0435\u0433\u043E\u0440\u0438\u044F", categories: availableCategories, selectedCategory: category, onCategoryChange: (catId) => setCategory(catId), className: formsStyle['form-group'] }) }), _jsxs("div", { className: formsStyle['form-row'], children: [_jsx(InputField, { label: "\u0421\u0443\u043C\u043C\u0430", inputType: "calc", value: amount, placeholder: '0', onSetValue: setAmount, required: true, autoFocus: true }), _jsx(InputField, { label: recurrence.enabled ? 'Дата начала' : 'Дата', value: txDate, onSetValue: setTxDate })] }), _jsx(InputField, { label: "\u041E\u043F\u0438\u0441\u0430\u043D\u0438\u0435", value: desc, placeholder: descPlaceholder, onSetValue: setDesc }), _jsx("div", { "data-tut": "tx-plan-toggle", children: _jsx(ToggleField, { isOn: isPlanned, label: 'Тип', onLabel: 'Плановая', offLabel: 'Фактическая', onToggleValue: setIsPlanned, className: formsStyle['form-group'] }) }), _jsx("div", { "data-tut": "tx-recurrence-toggle", children: _jsx(SwitchField, { isOn: recurrence.enabled, onToggle: (val) => setRecurrence(r => ({ ...r, enabled: val })), label: "\u0420\u0435\u0433\u0443\u043B\u044F\u0440\u043D\u0430\u044F \u043E\u043F\u0435\u0440\u0430\u0446\u0438\u044F", description: "\u0421\u043E\u0437\u0434\u0430\u0442\u044C \u0441\u0435\u0440\u0438\u044E \u043F\u043E\u0432\u0442\u043E\u0440\u044F\u044E\u0449\u0438\u0445\u0441\u044F \u043E\u043F\u0435\u0440\u0430\u0446\u0438\u0439", style: {
                        padding: '8px 0 10px',
                        borderTop: '1px solid var(--border-light)',
                        marginTop: 4,
                    } }) }), recurrence.enabled && (_jsxs("div", { "data-tut": "tx-recurrence-settings", children: [_jsx(ToggleField, { isOn: recurrence.frequency === 'weekly', label: "\u0427\u0430\u0441\u0442\u043E\u0442\u0430", onLabel: "\u0415\u0436\u0435\u043D\u0435\u0434\u0435\u043B\u044C\u043D\u0430\u044F", offLabel: "\u0415\u0436\u0435\u043C\u0435\u0441\u044F\u0447\u043D\u0430\u044F", onToggleValue: (isWeekly) => setRecurrence(r => ({
                            ...r,
                            frequency: isWeekly ? 'weekly' : 'monthly',
                            count: isWeekly ? 10 : 6,
                            monthDay: isWeekly ? r.monthDay : parseInt(txDate.split('-')[2]) || 1,
                        })), className: formsStyle['form-group'] }), _jsx(InputField, { label: recurrence.frequency === 'weekly' ? 'Кол-во недель' : 'Кол-во месяцев', value: recurrence.count === 0 ? '' : String(recurrence.count), onSetValue: (v) => {
                            const n = parseInt(v);
                            setRecurrence(r => ({ ...r, count: v === '' ? 0 : (Number.isNaN(n) ? r.count : Math.max(1, n)) }));
                        }, inputType: "amount" }), recurrence.frequency === 'weekly' ? (_jsx(WeekdayPicker, { label: "\u0414\u043D\u0438 \u043D\u0435\u0434\u0435\u043B\u0438", selected: recurrence.weekdays, onChange: (weekdays) => setRecurrence(r => ({ ...r, weekdays })), weekStart: weekStart })) : (_jsx(InputField, { label: "\u0414\u0435\u043D\u044C \u043C\u0435\u0441\u044F\u0446\u0430", value: recurrence.monthDay === 0 ? '' : String(recurrence.monthDay), onSetValue: (v) => {
                            const n = parseInt(v);
                            setRecurrence(r => ({ ...r, monthDay: v === '' ? 0 : (Number.isNaN(n) ? r.monthDay : Math.min(31, Math.max(1, n))) }));
                        }, inputType: "amount" }))] }))] }));
}
